import { useState, useEffect } from "react";
import {
  Trash2,
  ArrowLeft,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  generateKycConnect,
  getAllWallets,
  removeSubWallet,
} from "../../services/api/kyc.service";
import { useWallet } from "@solana/wallet-adapter-react";
import useNotification from "../../hooks/useNotification";
import { useDashboardData } from "../../hooks/useDashboardData";

interface Wallet {
  walletId: string;
  type: "Primary" | "Sub";
  createdAt: string;
}

export function WalletManagement() {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingWallet, setIsDeletingWallet] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { data } = useDashboardData();
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const { notify } = useNotification();

  useEffect(() => {
    if (publicKey) {
      loadWallets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey]);

  const loadWallets = async () => {
    if (!publicKey) return;

    try {
      setIsLoading(true);
      const walletsData = await getAllWallets(publicKey.toString());
      if (walletsData.length) {
        setWallets(walletsData);
      } else {
        setWallets([]);
      }
    } catch (error) {
      notify("Failed to load wallets", "error");
      console.error("Failed to load wallets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveWallet = async (subWalletId: string) => {
    try {
      setIsDeletingWallet(subWalletId);
      await removeSubWallet(subWalletId);
      notify("Wallet removed successfully", "success");
      await loadWallets();
    } catch (error) {
      notify("Failed to remove wallet", "error");
      console.error("Failed to remove wallet:", error);
    } finally {
      setIsDeletingWallet(null);
    }
  };

  const generateSubWalletLink = async () => {
    try {
      setIsGenerating(true);
      const id = await generateKycConnect();
      const link = `${window.location.origin}/code/${id}`;
      setGeneratedLink(link);
      notify("Link generated successfully", "success");
    } catch (error) {
      notify("Failed to generate link", "error");
      console.error("Failed to generate sub-wallet link:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-0 md:max-w-4xl mx-auto">
      <button
        onClick={() => navigate("/")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div className="bg-white border rounded-lg shadow p-4 md:p-6">
        {data?.isPrimary ? (
          <>
            <div className="mb-6">
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="w-full p-4 bg-gray-50 rounded-lg flex justify-between items-center hover:bg-gray-100 transition-colors border"
              >
                <div className="text-start">
                  <h3 className="text-lg font-bold font-body text-gray-900">
                    Multiple Wallets, One Verification
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isDescriptionExpanded
                      ? "Click to collapse"
                      : "Learn how to Link additional wallets under your main account."}
                  </p>
                </div>
                {isDescriptionExpanded ? (
                  <ChevronUp className="text-gray-500" />
                ) : (
                  <ChevronDown className="text-gray-500" />
                )}
              </button>

              {isDescriptionExpanded && (
                <div className="mt-2 p-4 bg-gray-50 rounded-lg text-start border">
                  <p className="text-sm text-gray-600 mb-3">
                    Want to use multiple wallets without verifying each one
                    separately? Link them all to your main verified account for
                    a quick and easy way to manage everything under one
                    identity--no extra steps needed!
                  </p>
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-1">Quick Guide:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>
                        Your current connected wallet is your main verified
                        wallet.
                      </li>
                      <li>
                        Hit "Generate Sub-wallet Link" to create an invite.
                      </li>
                      <li>
                        Open that link with your other wallet to connect it.
                      </li>
                      <li>
                        Remove connected wallets anytime with the trash icon.
                      </li>
                    </ol>
                  </div>
                </div>
              )}
            </div>

            <h2 className="text-lg md:text-xl font-semibold font-body mb-2 text-yellow-500">
              Connected Wallets
            </h2>
          </>
        ) : (
          <h2 className="text-lg md:text-xl font-semibold mb-4 text-yellow-500">
            Current Wallet
          </h2>
        )}

        <div className="mb-4">
          <div className="p-3 md:p-4 border border-gray-300 rounded-lg mb-3 md:mb-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
              <div>
                <p className="font-medium text-blue-500">Current</p>
                <p className="text-xs md:text-sm text-gray-600 break-all">
                  {publicKey?.toString()}
                </p>
              </div>
            </div>
          </div>

          {data?.isPrimary &&
            wallets
              .filter((item) => item.walletId !== publicKey?.toString())
              .map((item, index) => (
                <div
                  key={item.walletId}
                  className="p-3 md:p-4 border border-gray-300 rounded-lg mb-3 md:mb-4"
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                    <div>
                      <p className="font-medium text-gray-900">
                        Sub #{index + 1}
                      </p>
                      <p className="text-xs md:text-sm text-gray-600 break-all">
                        {item.walletId}
                      </p>
                    </div>
                    <button
                      className="text-gray-400 hover:text-red-500 transition-colors self-end md:self-center"
                      onClick={() => handleRemoveWallet(item.walletId)}
                      disabled={isDeletingWallet === item.walletId}
                    >
                      {isDeletingWallet === item.walletId ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
        </div>

        {data?.isPrimary && (
          <div className="space-y-4">
            <button
              onClick={generateSubWalletLink}
              disabled={isGenerating}
              className="w-full md:w-auto px-4 py-2 rounded-lg bg-zinc-900 text-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? "Generating..." : "Generate Sub-wallet Link"}
            </button>

            {generatedLink && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2 text-gray-700">
                  Share this link with the sub-wallet owner:
                </p>
                <div className="flex flex-col md:flex-row gap-2">
                  <input
                    type="text"
                    readOnly
                    value={generatedLink}
                    className="flex-1 p-2 border border-gray-300 rounded-lg text-sm break-all"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(generatedLink)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This link will expire after one use. Generate a new link for
                  each sub-wallet.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
